<template>
  <div class="container web-properties">
    <div class="web-properties__title-container">
      <div v-if="isDataFetched" class="web-properties__title-right">
        <div class="web-properties__urls">
          <span class="web-properties__count">URLs: {{totalItems}}</span>
          <base-select
            class="web-properties__grid-select"
            :options="paginationOptions"
            alignLeft
            :titleStyle="{'padding-left': '5px'}"
            :headerStyle="{}"
            showTopBorder
            shiftLabelRight
            v-model="pageItemsLimit"
            :selectStyle="{'max-height': '35px !important', width: '100px !important'}"
            @change="handleLimitChange"
          />
        </div>
        <button class="web-properties__button" @click="openEditModal('create')">
          <base-material-design-icon class="web-properties__button__plus" icon="plus"/>
          CREATE NEW
        </button>
      </div>
    </div>

    <div v-if="!isDataFetched">
      <div class="web-properties__no-data">Awaiting Data</div>
    </div>
    <template v-else>
      <div class="common-table web-properties__table">
        <div class="common-table__row common-table__header">
          <div class="common-table__header-cell"
               v-for="column in tableConfig"
               :key="column.property"
               :class="[`web-properties__${column.property}`]">
            <base-multi-select-sortable-header
              class="web-properties__select-header"
              :column="column"
              :sort-config="sortConfig"
              :predefinedOptions="filterParams[column.property]"
              @sort="onHeaderCellClicked(column, $event)"
              @apply="filterColumn($event, column)"
              @cancel="cancelFilter($event, column)"
              :ref="sortableHeaderRefs[column.property]"
            />
            <base-material-design-icon v-if="column.property === 'pattern'"
                                       v-tooltip.top-middle="{
                                          content: 'The % character is used for wild card matching in the system. It can be used anywhere in the web property pattern (beginning, middle or end).',
                                          ...rowTooltipOptions}"
                                       class="web-properties__tooltip"
                                       icon="questionCircle" />
          </div>
        </div>

        <template v-if="dataMissing">
          <div class="common-table__row no-data-block">
            No records found
          </div>
        </template>
        <template v-else>
          <template v-for="webProperty in paginatedWebProperties">
            <div class="common-table__row"
                 @click="openEditModal('edit', webProperty)"
                 :key="webProperty._id">
              <div class="common-table__cell"
                   v-for="column in tableConfig"
                   :key="column.property"
                   :class="[`web-properties__${column.property}`]">
                <div class="web-properties__pattern-row" v-if="column.property === 'pattern'">
                  <p v-for="pattern in webProperty[column.property]" :key="pattern">{{pattern.value}}</p>
                </div>
                <div v-else>
                  {{webProperty[column.property]}}
                </div>
              </div>
            </div>
          </template>
        </template>
      </div>
    </template>

    <div class="pagination-wrapper" v-show="hasPagination">
      <base-pagination :limit="pageItemsLimit"
                       :total-items="totalItems"
                       @pageChanged="pageChanged"/>
    </div>
    <div class="web-properties__update-loader" v-if="isDataUpdating">
      <base-loader class="web-properties__update-loader-ring"/>
    </div>
    <modal name="edit-modal" v-bind="modalProps">
      <EditWebPropertyModal :editModalType="editModalType"
                            :editModalData="editModalData"
                            :hasClientWebPropertyGroup="hasClientWebPropertyGroup"
                            @close="closeEditModal" />
    </modal>
  </div>
</template>

<script>
import BaseSortMixins from '@/common/BaseSortMixins'
import { mapActions, mapState } from 'vuex'
import EditWebPropertyModal from '@/features/web-property/EditWebPropertyModal'
import baseModalProps from '@/common/BaseModalProps'
import helper from '@/services/Helper'

const ITEMS_PER_PAGE = 100
const EMPTY_FILTERS = {
  page_from: 0,
  page_to: ITEMS_PER_PAGE,
  type: [],
  is_active: [],
  property_group_name: '',
  pattern: ''
}
// table config
const DEFAULT_CONFIG = [
  {
    label: 'Type',
    property: 'type',
    isSelectableValue: true, // this value could be selected using BaseSelect widget
    isSmartInput: false,
    options: []
  },
  {
    label: 'Property Group Name',
    property: 'property_group_name'
  },
  {
    label: 'Is Active',
    property: 'is_active',
    isSelectableValue: true, // this value could be selected using BaseSelect widget
    isSmartInput: false,
    options: [
      {
        label: 'Yes',
        value: 'Yes'
      },
      {
        label: 'No',
        value: 'No'
      }
    ]
  },
  {
    label: 'Pattern',
    property: 'pattern'
  }
]

export default {
  name: 'WebProperty',
  mixins: [BaseSortMixins],
  components: {
    EditWebPropertyModal
  },
  data () {
    return {
      isDataUpdating: false,
      isDataFetched: false,
      tableConfig: [...DEFAULT_CONFIG],
      filterParams: { ...EMPTY_FILTERS },
      pageItemsLimit: ITEMS_PER_PAGE,
      totalItems: 0,
      editModalData: {},
      editModalType: '',
      sortableHeaderRefs: {
        'type': 'type',
        'property_group_name': 'property_group_name',
        'pattern': 'pattern'
      },
      paginationOptions: [
        {
          label: '100',
          value: 100
        },
        {
          label: '250',
          value: 250
        },
        {
          label: '500',
          value: 500
        }
      ]
    }
  },
  watch: {
    clientId: {
      handler (clientId) {
        this.loadData(clientId)
      },
      immediate: true
    },
    webProperties: {
      handler (webProperties) {
        const options = {}
        this.tableConfig.forEach((v) => {
          // if options available - get property name
          if (v.options) {
            options[v.property] = []
          }
        })

        const properties = Object.keys(options)

        webProperties.forEach((webProperty) => {
          for (const property of properties) {
            !options[property].find(i => i.value === webProperty[property]) && options[property].push({
              value: webProperty[property],
              label: webProperty[property]
            })
          }
        })

        this.tableConfig = this.tableConfig.map((v) => {
          if (v.options) {
            v.options = options[v.property].sort() || []
          }
          return v
        })
      },
      deep: true
    }
  },
  computed: {
    ...mapState('webProperty', ['webProperties']),
    paginatedWebProperties () {
      let webProperties = [...this.webProperties]
      // eslint-disable-next-line camelcase
      const { page_from = 0, page_to = 100, property_group_name, pattern, type, is_active } = this.filterParams
      webProperties = webProperties.filter(webProperty => {
        const typeFilter = !type.length || type.includes(webProperty.type)
        const isActiveFilter = !is_active.length || is_active.includes(webProperty.is_active)
        // eslint-disable-next-line camelcase
        const propertyGroupNameFilter = typeof property_group_name !== 'string' || webProperty.property_group_name.toLowerCase().includes(property_group_name.toLowerCase())
        // eslint-disable-next-line camelcase
        const patternFilter = (typeof pattern !== 'string' || !pattern.length) || webProperty.pattern.some(i => i.value.toLowerCase().includes(pattern.toLowerCase()))

        return typeFilter && propertyGroupNameFilter && patternFilter && isActiveFilter
      })
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.totalItems = webProperties.length
      return helper.sortByMultipleParams(webProperties.slice(page_from, page_to), this.sortConfig)
    },
    hasClientWebPropertyGroup () {
      return this.webProperties.some(group => group.type.toLowerCase() === 'clients')
    },
    modalProps () {
      return { ...baseModalProps, height: 'auto', width: '610px' }
    },
    rowTooltipOptions () {
      const options = {
        classes: `rfx-tooltip-arrow`,
        trigger: 'hover'
      }
      return Object.assign({}, this.tooltipOptions, options)
    },
    hasPagination () {
      return (this.isDataFetched && !this.isDataUpdating) && this.totalItems > this.pageItemsLimit
    },
    dataMissing () {
      return this.isDataFetched && !this.webProperties.length
    }
  },
  methods: {
    ...mapActions('webProperty', ['getWebProperties']),
    async loadData (clientId) {
      if (!clientId) {
        return
      }

      this.isDataUpdating = true
      await this.getWebProperties({ clientId })
      this.isDataUpdating = false
      this.isDataFetched = true
    },
    handleLimitChange (value) {
      this.pageItemsLimit = value
      this.filterParams = { ...this.filterParams, page_from: 0, page_to: value }
    },
    openEditModal (type, webProperty) {
      this.editModalType = type
      this.editModalData = webProperty
      this.$modal.show('edit-modal')
    },
    closeEditModal (isUpdate = false) {
      this.$modal.hide('edit-modal')
      isUpdate && this.loadData(this.clientId)
    },
    pageChanged ({ from, to }) {
      this.filterParams.page_from = from
      this.filterParams.page_to = to
    },
    onHeaderCellClicked (cell, direction) {
      if (direction === 'clear') {
        this.sortConfig.shift()
        return
      }
      if (this.sortConfig.length) {
        this.sortConfig[0].propertyName = cell.property
        this.sortConfig[0].sortDirection = direction
      } else if (cell) {
        this.sortConfig = [{
          sortDirection: direction,
          propertyName: cell.property
        }]
      }
    },
    filterColumn: function (event, column) {
      this.filterParams[column.property] = event
      this.filterParams.page_from = 0
      this.filterParams.page_to = this.pageItemsLimit
    },
    cancelFilter (event, column) {
      this.filterParams[column.property] = null
    }
  }
}
</script>

<style scoped lang="scss">

  .common-table {
    border-radius: $rfx-default-border-radius;
    overflow: visible;

    &__row {
      &:hover {
        cursor: pointer;
        background-color: rgba(1, 22, 39, .1) !important;
      }
    }

    &__header {
      background-color: rgba(1, 22, 39, 0.1);
      border-top-right-radius: 4px;
      border-top-left-radius: 4px;
    }

    &__header-cell {
      color: $rfx-dark-march;
      font-weight: bold;
      padding: 0;
      height: 60px;
      display: flex;
      align-items: center;
      cursor: pointer;

      &:first-child {
        flex: 0 0 25%;

        &::v-deep .multi-select__title-container{
          padding-left: 27px;
        }
      }
    }

    &__cell {
      height: auto;
      padding: 23px 0 23px 13px;
      display: flex;
      align-items: flex-start;
      font-size: 14px;
      border-bottom: 1px solid $rfx-light-steel;

      &:first-child {
        padding-left: 25px;
      }

      &:last-child {
        padding-right: 25px;
      }
    }

    &__row {
      border-left: 1px solid $rfx-light-steel;
      border-right: 1px solid $rfx-light-steel;
    }
  }

  .web-properties {
    position: relative;
    min-height: 200vh !important;

    &__tooltip {
      font-size: 21px;
      position: absolute;
      left: 105px;
    }

    &__title-right {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      flex: 1 1 50%;
      height: 60px;
    }

    &__type,
    &__is_active,
    &__property_group_name {
      color: $rfx-dark-march;
    }

    &__title-right > *:not(:last-child) {
      margin-right: 30px;
    }

    &__title-left {
      display: flex;
      align-items: center;
    }

    &__title-left > *:not(:last-child) {
      margin-right: 30px;
    }

    &__grid-select {
      flex-basis: 100px;
    }

    &__title-container {
      padding: 14px 0;
      display: flex;
      align-items: center;
      justify-content: flex-end;
    }

    &__table {
      position: relative;
      display: flex;
      flex-direction: column;
    }

    &__update-loader {
      position: absolute;
      display: flex;
      width: 100%;
      height: 100%;
      justify-content: center;
      background-color: white;
      top: 0;
      opacity: 0.5;
      z-index: 10000;
    }

    &__update-loader-ring {
      margin-top: 200px;
    }

    &__pattern {
      &.common-table__header-cell {
        flex: 0 0 26.1%;
        position: relative;
      }
    }

    &__pattern-row {
      display: flex;
      flex-direction: column;

      p {
        margin: 5px 0;

        &:first-child {
          margin-top: 0;
        }

        &:last-child {
          margin-bottom: 0;
        }
      }
    }

    &__urls {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: space-between;
      height: 100%;
    }

    &__button {
      width: 136px;
      padding-left: 40px;
      padding-right: 20px;
      height: 60px;
      border-radius: 4px;
      font-size: 10px;
      font-weight: 900;
      text-transform: uppercase;
      border: none;
      outline: none;
      cursor: pointer;
      background-color: $rfx-medium-march;
      color: #fff;
      position: relative;

      &__plus {
        position: absolute;
        left: 18px;
        top: 23px;
        font-size: 15px;
      }
    }

    &__no-data {
      display: flex;
      height: 300px;
      background-color: #fff;
      border-radius: $rfx-default-border-radius;
      align-items: center;
      justify-content: center;
      font-size: 27px;
      font-weight: 600;
      color: $rfx-medium-grey;
      flex: 1 1;
    }

    .pagination-wrapper {
      display: flex;
      height: 60px;
      margin-top: 10px;
      justify-content: center;
      align-items: center;
      border: 1px solid $rfx-light-steel;
      border-radius: $rfx-default-border-radius;
    }

    .common-table {
      &__header-cell {
        color: $rfx-dark-march;
        font-weight: bold;
      }
    }
  }

</style>
